import React, { useState, useEffect } from 'react';
import { 
  FaBarcode, FaQrcode, FaTimes, FaCheck, FaStepForward,
  FaBox, FaWineGlass, FaUser, FaDollarSign, FaTrash, FaPlus
} from 'react-icons/fa';
import OutboundBarcodeScanner from './OutboundBarcodeScanner';
import Loader from '../ui/Loader';
import Message from '../ui/Message';
import api from '../../utils/api';
import PartnerSelector from './PartnerSelector';
import { useSelector } from 'react-redux';

const UnifiedOutboundModal = ({ onClose, onSuccess }) => {
  const [step, setStep] = useState('scanning'); // scanning, confirming, processing, success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  
  // 按产品分组的扫描结果
  const [scannedItems, setScannedItems] = useState({});
  // 产品信息设置
  const [productSettings, setProductSettings] = useState({});
  // 客户信息
  const [customerInfo, setCustomerInfo] = useState({
    customer: '',
    operator: '',
    notes: ''
  });

  const { user } = useSelector(state => state.auth);

  useEffect(() => {
    if (user) {
      setCustomerInfo(prev => ({
        ...prev,
        operator: user.full_name || user.phone_number || '未知用户'
      }));
    }
  }, [user]);

  // 处理扫码结果
  const handleCodeScan = async (code) => {
    if (!code) return;
    
    setLoading(true);
    try {
      // 检查是否已经扫描过此码
      const allScannedCodes = Object.values(scannedItems).flat().map(item => item.code);
      if (allScannedCodes.includes(code)) {
        setError(`条码 "${code}" 已经扫描过，不能重复扫描`);
        return;
      }

      // 处理URL格式的二维码，提取有效的编码部分
      const extractCodeFromUrl = (inputCode) => {
        try {
          // 如果是URL格式，尝试提取关键参数
          if (inputCode.toLowerCase().startsWith('http')) {
            const url = new URL(inputCode);
            // 尝试从不同的参数中提取编码
            const codeFromX = url.searchParams.get('X');
            const codeFromCode = url.searchParams.get('code');
            const codeFromId = url.searchParams.get('id');
            
            // 返回找到的第一个有效参数，或者URL路径的最后部分
            if (codeFromX) return codeFromX;
            if (codeFromCode) return codeFromCode;
            if (codeFromId) return codeFromId;
            
            // 如果没有找到参数，尝试从路径中提取
            const pathParts = url.pathname.split('/').filter(part => part);
            if (pathParts.length > 0) {
              return pathParts[pathParts.length - 1];
            }
          }
          
          // 如果不是URL格式，直接返回原始代码
          return inputCode;
        } catch (error) {
          console.warn('Failed to parse URL, using original code:', error);
          return inputCode;
        }
      };
      
      // 提取实际的产品编码
      const actualCode = extractCodeFromUrl(code);
      console.log('Original code:', code);
      console.log('Extracted code:', actualCode);

      let data;
      let itemType;
      
      // 尝试搜索单瓶码
      try {
        const unitResponse = await api.get(`/inventory/search/unit/${actualCode}`);
        data = unitResponse.data;
        
        if (data.unit.status !== 'available') {
          throw new Error(`此瓶已${data.unit.status === 'sold' ? '售出' : '不可用'}`);
        }
        
        itemType = 'unit';
      } catch (unitError) {
        // 检查是否是特定的单瓶码错误
        const errorDetail = unitError.response?.data?.detail || '';
        const isUnitSpecificError = errorDetail.includes('已于') || 
                                  errorDetail.includes('状态为') ||
                                  errorDetail.includes('已出库');
        
        if (isUnitSpecificError) {
          throw new Error(unitError.response.data.detail);
        }
        
        // 尝试搜索箱码
        try {
          const boxResponse = await api.get(`/inventory/search/box/${actualCode}`);
          data = boxResponse.data;
          
          // 添加调试信息和数据验证
          console.log('Box search response:', data);
          
          // 验证数据结构
          if (!data || !data.box) {
            throw new Error('箱码搜索返回的数据结构异常');
          }
          
          if (data.box.remaining_units === 0) {
            throw new Error('此箱已空，无可出库商品');
          }
          
          itemType = 'box';
        } catch (boxError) {
          let errorMessage;
          if (boxError.response?.status === 404) {
            errorMessage = `条码 ${actualCode} 未找到对应的入库记录`;
          } else {
            errorMessage = boxError.response?.data?.detail || 
                          boxError.response?.data?.error || 
                          boxError.message || 
                          '扫描失败，请重试';
          }
          throw new Error(errorMessage);
        }

        
      }

      // 添加到扫描结果中
      const productKey = `${data.product.id}_${data.warehouse.id}`;
      const newItem = {
        code: actualCode,
        originalCode: code,
        type: itemType,
        timestamp: new Date().toLocaleTimeString(),
        quantity: itemType === 'unit' ? 1 : data.box.remaining_units,
        details: itemType === 'unit' ? {
          unit_id: data.unit.id,
          unit_barcode: data.unit.unit_barcode,
          unit_qrcode: data.unit.unit_qrcode,
          box_barcode: data.box.box_barcode,
          box_qrcode: data.box.box_qrcode
        } : {
          box_id: data.box.id,
          box_barcode: data.box.box_barcode,
          box_qrcode: data.box.box_qrcode,
          available_units: data.availableUnits || []
        }
      };

      setScannedItems(prev => ({
        ...prev,
        [productKey]: {
          product: data.product,
          warehouse: data.warehouse,
          inbound: data.inbound,
          items: [...(prev[productKey]?.items || []), newItem]
        }
      }));

      // 如果是新产品，初始化设置
      if (!productSettings[productKey]) {
        setProductSettings(prev => ({
          ...prev,
          [productKey]: {
            unit_price: parseFloat(data.inbound?.unit_cost) || 0
          }
        }));
      }

      setShowBarcodeScanner(false);
      setError('');
      setSuccess(`已扫描：${data.product.chinese_name || data.product.name} (${itemType === 'unit' ? '1瓶' : data.box.remaining_units + '瓶整箱'})`);
      
    } catch (error) {
      console.error('Code scan error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // 删除扫描项
  const removeScannedItem = (productKey, itemIndex) => {
    setScannedItems(prev => {
      const updated = { ...prev };
      if (updated[productKey]) {
        updated[productKey] = {
          ...updated[productKey],
          items: updated[productKey].items.filter((_, index) => index !== itemIndex)
        };
        // 如果该产品没有扫描项了，删除整个产品
        if (updated[productKey].items.length === 0) {
          delete updated[productKey];
          // 同时删除设置
          setProductSettings(prev => {
            const newSettings = { ...prev };
            delete newSettings[productKey];
            return newSettings;
          });
        }
      }
      return updated;
    });
  };

  // 更新产品设置
  const updateProductSettings = (productKey, field, value) => {
    setProductSettings(prev => ({
      ...prev,
      [productKey]: {
        ...prev[productKey],
        [field]: value
      }
    }));
  };

  // 计算统计信息
  const calculateStats = () => {
    let totalItems = 0;
    let totalUnits = 0;
    let totalValue = 0;

    Object.entries(scannedItems).forEach(([productKey, productData]) => {
      const settings = productSettings[productKey] || {};
      const units = productData.items.reduce((sum, item) => sum + item.quantity, 0);
      const unitPrice = parseFloat(settings.unit_price) || 0;
      const value = units * unitPrice;
      
      totalItems += productData.items.length;
      totalUnits += units;
      totalValue += value;
    });

    return { totalItems, totalUnits, totalValue };
  };

  // 处理客户信息变更
  const handleCustomerInfoChange = (e) => {
    const { name, value } = e.target;
    setCustomerInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 确认出库
  const handleOutbound = async () => {
    const productKeys = Object.keys(scannedItems);
    if (productKeys.length === 0) {
      setError('请先扫描商品');
      return;
    }
    
    if (!customerInfo.customer || !customerInfo.operator) {
      setError('请填写客户名称和操作员');
      return;
    }

    // 检查所有产品是否都设置了单价
    const missingPrices = productKeys.filter(key => {
      const unitPrice = parseFloat(productSettings[key]?.unit_price) || 0;
      return unitPrice <= 0;
    });
    
    if (missingPrices.length > 0) {
      setError('请为所有产品设置单价');
      return;
    }
    
    setStep('processing');
    setLoading(true);
    
    try {
      // 转换为新的API格式
      const items = [];
      
      Object.entries(scannedItems).forEach(([productKey, productData]) => {
        const settings = productSettings[productKey];
        
        productData.items.forEach(item => {
          const unitPrice = parseFloat(settings.unit_price) || 0;
          items.push({
            type: item.type,
            product_id: productData.product.id,
            warehouse_id: productData.warehouse.id,
            quantity: item.quantity,
            unit_price: unitPrice,
            codes: [item.code],
            supplementary_units: [] // 暂时不支持补录
          });
        });
      });
      
      const outboundData = {
        items: items,
        customer: customerInfo.customer,
        operator: customerInfo.operator,
        notes: customerInfo.notes
      };
      
      await api.post('/inventory/outbound', outboundData);
      
      setStep('success');
      setSuccess('出库成功！');
      
      // 3秒后自动调用成功回调
      setTimeout(() => {
        if (onSuccess) onSuccess();
      }, 3000);
      
    } catch (error) {
      console.error('Outbound error:', error);
      setError(error.response?.data?.error || '出库失败，请重试');
      setStep('confirming');
    } finally {
      setLoading(false);
    }
  };

  // 重置并继续
  const resetAndContinue = () => {
    setStep('scanning');
    setScannedItems({});
    setProductSettings({});
    setCustomerInfo(prev => ({ ...prev, customer: '', notes: '' }));
    setError('');
    setSuccess('');
  };

  const stats = calculateStats();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* 头部 */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              📤 批量扫码出库
            </h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <FaTimes size={24} />
            </button>
          </div>

          {/* 步骤指示器 */}
          <div className="flex items-center justify-center mb-6">
            <div className={`flex items-center ${step === 'scanning' ? 'text-blue-600' : 'text-green-600'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step === 'scanning' ? 'bg-blue-600 text-white' : 'bg-green-600 text-white'
              }`}>1</div>
              <span className="ml-2">批量扫码</span>
            </div>
            <div className="w-16 h-0.5 bg-gray-300 mx-4"></div>
            <div className={`flex items-center ${
              step === 'confirming' ? 'text-blue-600' : 
              ['processing', 'success'].includes(step) ? 'text-green-600' : 'text-gray-400'
            }`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step === 'confirming' ? 'bg-blue-600 text-white' : 
                ['processing', 'success'].includes(step) ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-500'
              }`}>2</div>
              <span className="ml-2">确认信息</span>
            </div>
            <div className="w-16 h-0.5 bg-gray-300 mx-4"></div>
            <div className={`flex items-center ${step === 'success' ? 'text-green-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                step === 'success' ? 'bg-green-600 text-white' : 'bg-gray-300 text-gray-500'
              }`}>3</div>
              <span className="ml-2">完成</span>
            </div>
          </div>

          {/* 错误和成功消息 */}
          {error && <Message variant="error">{error}</Message>}
          {success && <Message variant="success">{success}</Message>}

          {/* 扫码阶段 */}
          {step === 'scanning' && (
            <div className="space-y-6">
              {/* 扫描操作区 */}
              <div className="border-2 border-dashed border-blue-300 rounded-lg p-6 text-center bg-blue-50">
                <div className="space-y-4">
                  <FaBarcode className="text-5xl text-blue-500 mx-auto" />
                  <h3 className="text-xl font-medium text-blue-800">连续扫描商品条码</h3>
                  <p className="text-blue-600">
                    支持瓶码和箱码混合扫描，系统自动按产品分组
                  </p>
                  <button
                    onClick={() => setShowBarcodeScanner(true)}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center mx-auto"
                  >
                    <FaBarcode className="mr-2" />
                    {Object.keys(scannedItems).length === 0 ? '开始扫描' : '继续扫描'}
                  </button>
                </div>
              </div>

              {/* 扫描结果展示 */}
              {Object.keys(scannedItems).length > 0 && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold">
                      扫描结果 ({Object.keys(scannedItems).length} 个产品，{stats.totalItems} 个条码，{stats.totalUnits} 瓶)
                    </h3>
                    <button
                      onClick={() => setStep('confirming')}
                      className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center"
                    >
                      <FaStepForward className="mr-2" />
                      下一步：确认信息
                    </button>
                  </div>

                  <div className="space-y-4">
                    {Object.entries(scannedItems).map(([productKey, productData]) => (
                      <div key={productKey} className="border rounded-lg overflow-hidden">
                        <div className="bg-gray-50 px-4 py-3 border-b">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="w-12 h-12 mr-3">
                                <img
                                  src={productData.product.image_url || '/images/default-wine.jpg'}
                                  alt={productData.product.name}
                                  className="w-full h-full object-cover rounded"
                                />
                              </div>
                              <div>
                                <h4 className="font-semibold">
                                  {productData.product.chinese_name || productData.product.name}
                                </h4>
                                <p className="text-sm text-gray-600">
                                  {productData.warehouse.name} | 共扫描 {productData.items.length} 个码 | 
                                  总数量 {productData.items.reduce((sum, item) => sum + item.quantity, 0)} 瓶
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <div className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {productData.items.map((item, index) => (
                              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded border">
                                <div className="flex items-center">
                                  {item.type === 'unit' ? 
                                    <FaWineGlass className="text-blue-500 mr-2" /> : 
                                    <FaBox className="text-green-500 mr-2" />
                                  }
                                  <div>
                                    <div className="font-mono text-sm">{item.code}</div>
                                    <div className="text-xs text-gray-500">
                                      {item.type === 'unit' ? '1瓶' : `${item.quantity}瓶(整箱)`} | {item.timestamp}
                                    </div>
                                  </div>
                                </div>
                                <button
                                  onClick={() => removeScannedItem(productKey, index)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <FaTimes size={14} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 底部操作 */}
              <div className="flex justify-between">
                <button
                  onClick={onClose}
                  className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                >
                  取消
                </button>
                
                {Object.keys(scannedItems).length > 0 && (
                  <button
                    onClick={() => setStep('confirming')}
                    className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700"
                  >
                    确认扫描结果，进入下一步
                  </button>
                )}
              </div>
            </div>
          )}

          {/* 确认阶段 */}
          {step === 'confirming' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">确认出库信息</h3>
                <button
                  onClick={() => setStep('scanning')}
                  className="text-blue-600 hover:text-blue-800"
                >
                  ← 返回扫码
                </button>
              </div>

              {/* 产品信息设置 */}
              <div className="space-y-4">
                {Object.entries(scannedItems).map(([productKey, productData]) => {
                  const settings = productSettings[productKey] || {};
                  const totalQuantity = productData.items.reduce((sum, item) => sum + item.quantity, 0);
                  const unitPrice = parseFloat(settings.unit_price) || 0;
                  const totalPrice = totalQuantity * unitPrice;
                  
                  return (
                    <div key={productKey} className="border rounded-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                        <div className="flex items-center">
                          <div className="w-12 h-12 mr-3">
                            <img
                              src={productData.product.image_url || '/images/default-wine.jpg'}
                              alt={productData.product.name}
                              className="w-full h-full object-cover rounded"
                            />
                          </div>
                          <div>
                            <h4 className="font-semibold">
                              {productData.product.chinese_name || productData.product.name}
                            </h4>
                            <p className="text-sm text-gray-600">
                              {productData.warehouse.name} | {totalQuantity} 瓶
                            </p>
                          </div>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            单价 (¥/瓶) *
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={settings.unit_price || ''}
                            onChange={(e) => updateProductSettings(productKey, 'unit_price', parseFloat(e.target.value) || 0)}
                            className="w-full p-2 border rounded-lg"
                            placeholder={`建议: ¥${productData.inbound?.unit_cost || 0}`}
                          />
                        </div>
                        
                        <div className="text-right">
                          <div className="text-lg font-bold text-green-600">
                            ¥{totalPrice.toFixed(2)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {totalQuantity} 瓶 × ¥{unitPrice.toFixed(2)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* 客户信息 */}
              <div className="border rounded-lg p-4">
                <h4 className="font-medium mb-4">客户信息</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      客户名称 *
                    </label>
                    <PartnerSelector
                      value={customerInfo.customer}
                      onChange={(value) => handleCustomerInfoChange({ target: { name: 'customer', value } })}
                      type="customer"
                      required={true}
                      allowCreate={true}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      操作员
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={customerInfo.operator}
                        className="w-full p-2 border rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed"
                        readOnly
                      />
                      <div className="absolute right-2 top-2">
                        <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">
                          当前用户
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    备注
                  </label>
                  <textarea
                    name="notes"
                    value={customerInfo.notes}
                    onChange={handleCustomerInfoChange}
                    rows={3}
                    className="w-full p-2 border rounded-lg"
                    placeholder="出库备注..."
                  />
                </div>
              </div>

              {/* 总计 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-blue-600">{stats.totalItems}</div>
                    <div className="text-sm text-gray-600">扫描条码数</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-600">{stats.totalUnits}</div>
                    <div className="text-sm text-gray-600">出库瓶数</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-600">¥{stats.totalValue.toFixed(2)}</div>
                    <div className="text-sm text-gray-600">总金额</div>
                  </div>
                </div>
              </div>

              {/* 操作按钮 */}
              <div className="flex justify-between">
                <button
                  onClick={() => setStep('scanning')}
                  className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                >
                  返回扫码
                </button>
                
                <div className="flex space-x-3">
                  <button
                    onClick={onClose}
                    className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleOutbound}
                    disabled={loading}
                    className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center"
                  >
                    {loading ? <Loader size="sm" /> : (
                      <>
                        <FaCheck className="mr-2" />
                        确认出库
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 处理中阶段 */}
          {step === 'processing' && (
            <div className="text-center space-y-6">
              <Loader size="lg" />
              <div>
                <h3 className="text-xl font-bold text-blue-800 mb-2">正在处理出库...</h3>
                <p className="text-gray-600">
                  正在出库 {stats.totalUnits} 瓶商品，请稍候
                </p>
              </div>
            </div>
          )}

          {/* 成功阶段 */}
          {step === 'success' && (
            <div className="text-center space-y-6">
              <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto">
                <FaCheck className="text-4xl text-green-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-green-800 mb-2">出库成功!</h3>
                <div className="text-gray-600 space-y-1">
                  <p>共出库 {stats.totalUnits} 瓶商品</p>
                  <p>扫描条码 {stats.totalItems} 个</p>
                  <p>总金额 ¥{stats.totalValue.toFixed(2)}</p>
                  <p>客户: {customerInfo.customer}</p>
                </div>
              </div>
              
              <div className="flex justify-center space-x-4">
                <button
                  onClick={resetAndContinue}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center"
                >
                  <FaStepForward className="mr-2" />
                  继续出库
                </button>
                <button
                  onClick={onClose}
                  className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700"
                >
                  完成
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* 条码扫描器 */}
      {showBarcodeScanner && (
        <OutboundBarcodeScanner
          onComplete={handleCodeScan}
          onClose={() => setShowBarcodeScanner(false)}
          title="连续扫描商品条码"
        />
      )}
    </div>
  );
};

export default UnifiedOutboundModal;